import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Divider, Button, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  FormControl, InputLabel, Select, MenuItem, Chip, Avatar,
  Tabs, Tab, Paper, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import {
  TrendingUp, TrendingDown, Print, PictureAsPdf, TableChart, BarChart, PieChart,
  AttachMoney, Assessment, AccountBalance, ShoppingCart, Inventory, 
  Egg, Pets, Timeline, Business
} from '@mui/icons-material';
import { useData } from '../contexts/DataContext';
import { formatCurrency } from '../utils/currency';

const Reports = () => {
  const { sales, batches, eggProduction, expenditures, incubationBatches, hatchingRecords, eggCollections } = useData();
  const [reportType, setReportType] = useState(0);
  const [bankingData, setBankingData] = useState({ accounts: [], deposits: [], withdrawals: [], cashFlow: [] });
  const [purchaseOrders, setPurchaseOrders] = useState([]);

  // Load banking and purchase order data
  useEffect(() => {
    const loadBankingData = async () => {
      try {
        const response = await fetch('/api/banking/summary');
        if (response.ok) {
          const data = await response.json();
          setBankingData(data);
        }
      } catch (error) {
        console.error('Failed to load banking data:', error);
      }
    };

    const loadPurchaseOrders = async () => {
      try {
        const response = await fetch('/api/purchase-orders');
        if (response.ok) {
          const data = await response.json();
          setPurchaseOrders(data);
        }
      } catch (error) {
        console.error('Failed to load purchase orders:', error);
      }
    };

    loadBankingData();
    loadPurchaseOrders();
  }, []);

  const financialAnalytics = useMemo(() => {
    const totalRevenue = (sales || []).reduce((sum, sale) => sum + (sale.total_amount || 0), 0);
    const totalExpenditure = (expenditures || []).reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
    const purchaseOrderTotal = (purchaseOrders || []).reduce((sum, po) => sum + (po.total_amount || 0), 0);
    const totalBankDeposits = (bankingData?.deposits || []).reduce((sum, deposit) => sum + (deposit.amount || 0), 0);
    const totalBankWithdrawals = (bankingData?.withdrawals || []).reduce((sum, withdrawal) => sum + (withdrawal.amount || 0), 0);
    
    const netIncome = totalRevenue - totalExpenditure - purchaseOrderTotal;
    const profitMargin = totalRevenue > 0 ? ((netIncome / totalRevenue) * 100).toFixed(1) : 0;
    const cashFlow = totalBankDeposits - totalBankWithdrawals;

    return {
      totalRevenue,
      totalExpenditure,
      purchaseOrderTotal,
      netIncome,
      profitMargin,
      totalBankDeposits,
      totalBankWithdrawals,
      cashFlow
    };
  }, [sales, expenditures, purchaseOrders, bankingData]);

  const productionAnalytics = useMemo(() => {
    const totalBirds = (batches || []).reduce((sum, batch) => sum + (batch.current_count || 0), 0);
    const totalEggsCollected = (eggCollections || []).reduce((sum, collection) => sum + (collection.eggs_collected || 0), 0);
    const totalEggsIncubated = (incubationBatches || []).reduce((sum, batch) => sum + (batch.total_eggs_set || 0), 0);
    const totalChicksHatched = (hatchingRecords || []).reduce((sum, record) => sum + (record.quantity_received || 0), 0);
    const activeBatches = batches.filter(b => b.status === 'Active').length;
    const activeIncubators = incubationBatches.filter(b => b.status === 'incubating').length;

    return {
      totalBirds,
      totalEggsCollected,
      totalEggsIncubated,
      totalChicksHatched,
      activeBatches,
      activeIncubators
    };
  }, [batches, eggCollections, incubationBatches, hatchingRecords]);

  const handleExport = (format) => {
    let content, filename;
    
    if (format === 'csv') {
      content = [
        ['Metric', 'Value', 'Percentage'].join(','),
        ['Total Revenue', financialAnalytics.totalRevenue, '100%'].join(','),
        ['Total Expenditure', financialAnalytics.totalExpenditure, ((financialAnalytics.totalExpenditure / financialAnalytics.totalRevenue) * 100).toFixed(1) + '%'].join(','),
        ['Purchase Orders', financialAnalytics.purchaseOrderTotal, ((financialAnalytics.purchaseOrderTotal / financialAnalytics.totalRevenue) * 100).toFixed(1) + '%'].join(','),
        ['Net Income', financialAnalytics.netIncome, financialAnalytics.profitMargin + '%'].join(','),
        ['Bank Deposits', financialAnalytics.totalBankDeposits, ''].join(','),
        ['Bank Withdrawals', financialAnalytics.totalBankWithdrawals, ''].join(','),
        ['Cash Flow', financialAnalytics.cashFlow, ''].join(',')
      ].join('\n');
      
      filename = `financial-report-${new Date().toISOString().split('T')[0]}.csv`;
    } else {
      // For PDF/Print, we'll use the browser's print functionality
      window.print();
      return;
    }
    
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Reports & Analytics</Typography>
      <Divider sx={{ mb: 3 }} />

      <Tabs value={reportType} onChange={(e, newValue) => setReportType(newValue)} sx={{ mb: 3 }}>
        <Tab label="Financial Overview" icon={<AttachMoney />} />
        <Tab label="Production Analytics" icon={<Assessment />} />
        <Tab label="Banking Summary" icon={<AccountBalance />} />
        <Tab label="Purchase Orders" icon={<ShoppingCart />} />
      </Tabs>

      {/* Financial Overview Tab */}
      {reportType === 0 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Revenue
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        {formatCurrency(financialAnalytics.totalRevenue)}
                      </Typography>
                    </Box>
                    <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Expenditure
                      </Typography>
                      <Typography variant="h5" color="error.main">
                        {formatCurrency(financialAnalytics.totalExpenditure)}
                      </Typography>
                    </Box>
                    <TrendingDown sx={{ fontSize: 40, color: 'error.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Net Income
                      </Typography>
                      <Typography variant="h5" color={financialAnalytics.netIncome >= 0 ? 'success.main' : 'error.main'}>
                        {formatCurrency(financialAnalytics.netIncome)}
                      </Typography>
                    </Box>
                    <BarChart sx={{ fontSize: 40, color: financialAnalytics.netIncome >= 0 ? 'success.main' : 'error.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Profit Margin
                      </Typography>
                      <Typography variant="h5" color={financialAnalytics.profitMargin >= 0 ? 'success.main' : 'error.main'}>
                        {financialAnalytics.profitMargin}%
                      </Typography>
                    </Box>
                    <PieChart sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <TableContainer component={Card}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Metric</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="right">%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Total Revenue</TableCell>
                  <TableCell align="right">{formatCurrency(financialAnalytics.totalRevenue)}</TableCell>
                  <TableCell align="right">100%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Total Expenditure</TableCell>
                  <TableCell align="right">{formatCurrency(financialAnalytics.totalExpenditure)}</TableCell>
                  <TableCell align="right">
                    {financialAnalytics.totalRevenue > 0 ? ((financialAnalytics.totalExpenditure / financialAnalytics.totalRevenue) * 100).toFixed(1) : 0}%
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Purchase Orders</TableCell>
                  <TableCell align="right">{formatCurrency(financialAnalytics.purchaseOrderTotal)}</TableCell>
                  <TableCell align="right">
                    {financialAnalytics.totalRevenue > 0 ? ((financialAnalytics.purchaseOrderTotal / financialAnalytics.totalRevenue) * 100).toFixed(1) : 0}%
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Net Income</TableCell>
                  <TableCell align="right">{formatCurrency(financialAnalytics.netIncome)}</TableCell>
                  <TableCell align="right">{financialAnalytics.profitMargin}%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Production Analytics Tab */}
      {reportType === 1 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Pets sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Birds
                      </Typography>
                      <Typography variant="h5">
                        {productionAnalytics.totalBirds}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Egg sx={{ fontSize: 40, color: 'warning.main' }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Eggs Collected
                      </Typography>
                      <Typography variant="h5">
                        {productionAnalytics.totalEggsCollected}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Timeline sx={{ fontSize: 40, color: 'info.main' }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Chicks Hatched
                      </Typography>
                      <Typography variant="h5">
                        {productionAnalytics.totalChicksHatched}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Active Operations</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Business /></ListItemIcon>
                      <ListItemText 
                        primary="Active Batches" 
                        secondary={`${productionAnalytics.activeBatches} batches currently active`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Assessment /></ListItemIcon>
                      <ListItemText 
                        primary="Active Incubators" 
                        secondary={`${productionAnalytics.activeIncubators} incubation batches running`} 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Egg /></ListItemIcon>
                      <ListItemText 
                        primary="Eggs in Incubation" 
                        secondary={`${productionAnalytics.totalEggsIncubated} eggs currently incubating`} 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Production Efficiency</Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><BarChart /></ListItemIcon>
                      <ListItemText 
                        primary="Hatching Success Rate" 
                        secondary={
                          productionAnalytics.totalEggsIncubated > 0 ? 
                          `${((productionAnalytics.totalChicksHatched / productionAnalytics.totalEggsIncubated) * 100).toFixed(1)}%` :
                          'No data'
                        } 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><TrendingUp /></ListItemIcon>
                      <ListItemText 
                        primary="Average Eggs per Collection" 
                        secondary={
                          eggCollections.length > 0 ? 
                          (productionAnalytics.totalEggsCollected / eggCollections.length).toFixed(1) :
                          'No data'
                        } 
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Banking Summary Tab */}
      {reportType === 2 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUp sx={{ fontSize: 40, color: 'success.main' }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Deposits
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        {formatCurrency(financialAnalytics.totalBankDeposits)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingDown sx={{ fontSize: 40, color: 'error.main' }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Total Withdrawals
                      </Typography>
                      <Typography variant="h5" color="error.main">
                        {formatCurrency(financialAnalytics.totalBankWithdrawals)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <AccountBalance sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="body2">
                        Net Cash Flow
                      </Typography>
                      <Typography variant="h5" color={financialAnalytics.cashFlow >= 0 ? 'success.main' : 'error.main'}>
                        {formatCurrency(financialAnalytics.cashFlow)}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Bank Accounts Summary</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Account Name</TableCell>
                      <TableCell>Account Number</TableCell>
                      <TableCell>Bank</TableCell>
                      <TableCell align="right">Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bankingData.accounts.map(account => (
                      <TableRow key={account.id}>
                        <TableCell>{account.account_name}</TableCell>
                        <TableCell>{account.account_number}</TableCell>
                        <TableCell>{account.bank_name}</TableCell>
                        <TableCell align="right">{formatCurrency(account.balance || 0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Purchase Orders Tab */}
      {reportType === 3 && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Total Purchase Orders</Typography>
                  <Typography variant="h5">{purchaseOrders.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Total Value</Typography>
                  <Typography variant="h5" color="primary">
                    {formatCurrency(financialAnalytics.purchaseOrderTotal)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>Pending Orders</Typography>
                  <Typography variant="h5" color="warning.main">
                    {purchaseOrders.filter(po => po.status === 'pending').length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Recent Purchase Orders</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Items</TableCell>
                      <TableCell align="right">Total Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseOrders.slice(0, 10).map(po => (
                      <TableRow key={po.id}>
                        <TableCell>{new Date(po.order_date).toLocaleDateString()}</TableCell>
                        <TableCell>{po.supplier_name}</TableCell>
                        <TableCell>{po.items?.length || 0} items</TableCell>
                        <TableCell align="right">{formatCurrency(po.total_amount)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={po.status} 
                            color={
                              po.status === 'completed' ? 'success' :
                              po.status === 'pending' ? 'warning' : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Export Buttons */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
        <Button 
          variant="contained" 
          color="error" 
          onClick={() => handleExport('pdf')} 
          startIcon={<PictureAsPdf />}
        >
          Export to PDF
        </Button>
        <Button 
          variant="contained" 
          color="success" 
          onClick={() => handleExport('csv')} 
          startIcon={<TableChart />}
        >
          Export to CSV
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => window.print()} 
          startIcon={<Print />}
        >
          Print
        </Button>
      </Box>
    </Box>
  );
};

export default Reports;
